import { handleApiCall } from '../api';
import { store } from '../../Redux/store';

export class FCMApiService {
  /** Dedupe identical register calls (user vs per-store) in one session. */
  private lastRegisterKey: string | null = null;

  clearRegistrationDedupeState(): void {
    this.lastRegisterKey = null;
  }

  /** Profile `notification_scope` from backend; default store when fulfilled but unset. */
  private resolveScope(): 'user' | 'store' | 'deferred' {
    const rp = (store.getState() as any)?.roleAndPermission;
    if (rp?.loading !== 'fulfilled') {
      return 'deferred';
    }
    const sc = rp.notification_scope;
    if (sc === 'user' || sc === 'store') {
      return sc;
    }
    return 'store';
  }

  private fcmTokenForRequest(): string | null {
    try {
      const t = (store.getState() as any)?.fcm?.token;
      if (t) {
        return t;
      }
    } catch {
      /* ignore */
    }
    try {
      return localStorage.getItem('fcm_token');
    } catch {
      return null;
    }
  }

  /**
   * Get or create a unique device ID for this browser/device
   * Stored in localStorage to persist across sessions
   */
  private getDeviceId(): string {
    const STORAGE_KEY = 'fcm_device_id';
    let deviceId = localStorage.getItem(STORAGE_KEY);
    
    if (!deviceId) {
      // Generate a unique device ID based on browser fingerprint
      // Using a combination of user agent, screen resolution, and timezone
      const fingerprint = [
        navigator.userAgent,
        window.screen.width + 'x' + window.screen.height,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        navigator.language,
        Date.now()
      ].join('|');
      
      // Create a simple hash (not cryptographically secure, but sufficient for device ID)
      deviceId = btoa(fingerprint).substring(0, 32);
      localStorage.setItem(STORAGE_KEY, deviceId);
      console.log('Generated new device ID:', deviceId);
    }
    
    return deviceId;
  }

  /**
   * Detect browser type from user agent
   * ✅ ENHANCED: Added Brave browser detection
   */
  private getBrowser(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Check for Brave browser first (it identifies as Chrome)
    if (userAgent.includes('brave') || (navigator as any).brave?.isBrave) {
      return 'brave';
    } else if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      return 'chrome';
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      return 'safari';
    } else if (userAgent.includes('firefox')) {
      return 'firefox';
    } else if (userAgent.includes('edg')) {
      return 'edge';
    } else if (userAgent.includes('opera') || userAgent.includes('opr')) {
      return 'opera';
    } else {
      return 'other';
    }
  }

  /**
   * Build registration payload with all required fields
   */
  private buildRegistrationPayload(token: string, storeId: string | number) {
    return {
      token,
      store_id: typeof storeId === 'string' ? parseInt(storeId, 10) : storeId,
      device_id: this.getDeviceId(),
      device_type: 'web',
      browser: this.getBrowser(),
    };
  }

  /** POST /notifications/register-user-token (user notification scope). */
  private async registerUserToken(token: string): Promise<void> {
    const dedupeKey = `u:${token}`;
    if (this.lastRegisterKey === dedupeKey) {
      return;
    }
    const payload = {
      token,
      device_id: this.getDeviceId(),
      device_type: 'web',
      browser: this.getBrowser(),
    };
    const response = await handleApiCall(
      'post',
      '/notifications/register-user-token',
      payload,
      { skipDeduplication: true },
    );
    this.lastRegisterKey = dedupeKey;
    console.log('FCM token registered successfully (user):', response);
  }

  /**
   * DELETE /notifications/unregister-user-token for this browser.
   * @param onlyIfTokenPresent — skip the request when no token is available (e.g. deferred cleanup).
   */
  private async unregisterUserToken(options?: {
    onlyIfTokenPresent?: boolean;
  }): Promise<any> {
    const t = this.fcmTokenForRequest();
    if (options?.onlyIfTokenPresent && !t) {
      return undefined;
    }
    return handleApiCall(
      'delete',
      '/notifications/unregister-user-token',
      { data: t ? { token: t } : {} },
      { skipDeduplication: true },
    );
  }

  async registerToken(token: string): Promise<void> {
    try {
      const scope = this.resolveScope();
      if (scope === 'deferred') {
        console.log('[FCMApiService] Skip FCM register until profile is loaded');
        return;
      }

      if (scope === 'user') {
        await this.registerUserToken(token);
        return;
      }

      const storeId = this.getStoreId();
      if (!storeId) {
        throw new Error('Store ID not found');
      }

      const dedupeKey = `s:${storeId}:${token}`;
      if (this.lastRegisterKey === dedupeKey) {
        return;
      }

      const payload = this.buildRegistrationPayload(token, storeId);
      console.log('Registering FCM token:', {
        token: token.substring(0, 20) + '...',
        storeId,
        device_id: payload.device_id,
        device_type: payload.device_type,
        browser: payload.browser,
      });

      const response = await handleApiCall(
        'post',
        '/notifications/register-store-token',
        payload,
        { skipDeduplication: true },
      );

      this.lastRegisterKey = dedupeKey;
      console.log('FCM token registered successfully:', response);
    } catch (error) {
      console.error('Failed to register FCM token:', error);
      throw error;
    }
  }

  async unregisterToken(): Promise<void> {
    try {
      const scope = this.resolveScope();

      if (scope === 'user') {
        console.log('Unregistering FCM user token');
        const response = await this.unregisterUserToken();
        this.clearRegistrationDedupeState();
        console.log('FCM token unregistered successfully:', response);
        return;
      }

      if (scope === 'deferred') {
        const storeId = this.getStoreId();
        const t = this.fcmTokenForRequest();
        if (storeId) {
          const response = await handleApiCall(
            'delete',
            `/notifications/unregister-store-token/${storeId}`,
            { data: t ? { token: t } : {} },
          );
          console.log('FCM token unregistered successfully (deferred/store):', response);
        } else if (t) {
          await this.unregisterUserToken();
        }
        this.clearRegistrationDedupeState();
        return;
      }

      const storeId = this.getStoreId();
      if (!storeId) {
        throw new Error('Store ID not found');
      }

      console.log('Unregistering FCM token for store:', storeId);

      const t = this.fcmTokenForRequest();
      const response = await handleApiCall(
        'delete',
        `/notifications/unregister-store-token/${storeId}`,
        { data: t ? { token: t } : {} },
      );

      this.clearRegistrationDedupeState();
      console.log('FCM token unregistered successfully:', response);
    } catch (error) {
      console.error('Failed to unregister FCM token:', error);
      throw error;
    }
  }

  async unregisterTokenForStores(storeIds: (string | number)[]): Promise<void> {
    const scope = this.resolveScope();

    if (scope === 'user') {
      try {
        await this.unregisterUserToken();
      } catch (e) {
        console.warn('[FCMApiService] User FCM unregister failed:', e);
      }
      this.clearRegistrationDedupeState();
      return;
    }

    const normalizedStoreIds = Array.from(
      new Set(
        (storeIds || [])
          .map((id) => String(id))
          .filter((id) => !!id && id !== 'null' && id !== 'undefined'),
      ),
    );

    if (normalizedStoreIds.length === 0) {
      if (scope === 'deferred') {
        try {
          await this.unregisterUserToken({ onlyIfTokenPresent: true });
        } catch {
          /* ignore */
        }
        this.clearRegistrationDedupeState();
        return;
      }
      console.warn('[FCMApiService] No valid store IDs provided for unregister');
      return;
    }

    // Prefer single bulk endpoint to avoid one request per store during logout.
    // IMPORTANT: Scope bulk unregister to THIS browser's token only, so that
    // logging out in one browser (e.g. Edge) does not deactivate tokens
    // registered from another browser (e.g. Chrome) on the same system.
    // Fallback to legacy per-store requests for backward compatibility.
    try {
      // Try to get the current FCM token for this browser session
      // so backend can restrict cleanup to this token only.
      let currentToken: string | null = null;
      try {
        const state: any = store.getState();
        currentToken = state?.fcm?.token || null;
      } catch {
        currentToken = null;
      }

      await handleApiCall(
        'post',
        '/notifications/unregister-store-token-all',
        currentToken ? { token: currentToken } : {},
      );
      console.log(
        `[FCMApiService] Bulk logout unregister completed for ${normalizedStoreIds.length} store(s)`,
      );
      this.clearRegistrationDedupeState();
      return;
    } catch (bulkError) {
      console.warn(
        '[FCMApiService] Bulk unregister unavailable, falling back to per-store unregister:',
        bulkError,
      );
    }

    const results = await Promise.allSettled(
      normalizedStoreIds.map((storeId) =>
        handleApiCall('delete', `/notifications/unregister-store-token/${storeId}`),
      ),
    );

    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length > 0) {
      console.warn(
        `[FCMApiService] Unregister failed for ${failed.length}/${normalizedStoreIds.length} stores`,
      );
    } else {
      console.log(
        `[FCMApiService] Unregistered FCM token for ${normalizedStoreIds.length} store(s) successfully`,
      );
    }
    this.clearRegistrationDedupeState();
  }

  async registerTokenForStore(token: string, storeId: string): Promise<void> {
    try {
      if (this.resolveScope() === 'user') {
        await this.registerUserToken(token);
        return;
      }

      const dedupeKey = `s:${storeId}:${token}`;
      if (this.lastRegisterKey === dedupeKey) {
        return;
      }

      const payload = this.buildRegistrationPayload(token, storeId);
      console.log('Registering FCM token for store:', {
        token: token.substring(0, 20) + '...',
        storeId,
        device_id: payload.device_id,
        device_type: payload.device_type,
        browser: payload.browser,
      });

      const response = await handleApiCall(
        'post',
        '/notifications/register-store-token',
        payload,
        { skipDeduplication: true },
      );

      this.lastRegisterKey = dedupeKey;
      console.log('✅ FCM token registered successfully for store:', storeId);
      console.log('FCM token registered successfully:', response);
    } catch (error) {
      console.error('Failed to register FCM token for store:', error);
      throw error;
    }
  }

  async registerTokenForNewStore(token: string, oldStoreId: string, newStoreId: string): Promise<void> {
    try {
      if (this.resolveScope() === 'user') {
        await this.registerUserToken(token);
        return;
      }

      console.log('🔄 Switching active store from:', oldStoreId, 'to:', newStoreId);
      console.log('ℹ️ Keeping existing store registrations and adding registration for new store');

      const dedupeKey = `s:${newStoreId}:${token}`;
      if (this.lastRegisterKey === dedupeKey) {
        return;
      }

      // Register for new store with full payload.
      // Do NOT unregister old store token mapping here:
      // one browser token can (and should) stay mapped to multiple stores.
      const payload = this.buildRegistrationPayload(token, newStoreId);
      const response = await handleApiCall(
        'post',
        '/notifications/register-store-token',
        payload,
        { skipDeduplication: true },
      );

      this.lastRegisterKey = dedupeKey;
      console.log('✅ Registered FCM token for new active store:', newStoreId);
      console.log('FCM token registered successfully:', response);
    } catch (error) {
      console.error('Failed to switch FCM token registration:', error);
      throw error;
    }
  }

  async getNotificationHistory(page: number = 1, limit: number = 10): Promise<any> {
    try {
      const storeId = this.getStoreId();
      if (!storeId) {
        throw new Error('Store ID not found');
      }

      console.log('Fetching notification history:', { storeId, page, limit });

      const response = await handleApiCall('GET', `/notifications/store/${storeId}`, {
        page,
        limit,
      });
      
      console.log('Notification history fetched successfully:', response);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch notification history:', error);
      throw error;
    }
  }

  async markAsRead(notificationId: string): Promise<void> {
    try {
      console.log('Marking notification as read:', notificationId);

      const response = await handleApiCall('PUT', `/notifications/${notificationId}/read`);
      
      console.log('Notification marked as read successfully:', response);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      throw error;
    }
  }

  private getStoreId(): string | null {
    // Get store ID from CURRENT_STORE_ID first (what user is actually viewing),
    // then fall back to profileStoreId[0] and legacy storeId for backward compatibility.
    const state: any = store.getState();

    const currentStoreIdFromLS = localStorage.getItem('CURRENT_STORE_ID');
    const profileStoreId =
      state?.roleAndPermission?.profileStoreId &&
      state.roleAndPermission.profileStoreId.length > 0
        ? state.roleAndPermission.profileStoreId[0]?.id
        : null;
    const legacyStoreId = localStorage.getItem('storeId');

    const resolvedStoreId =
      currentStoreIdFromLS ||
      (profileStoreId != null ? String(profileStoreId) : null) ||
      legacyStoreId;

    console.log('[FCMApiService] Store ID resolved for token registration:', {
      resolvedStoreId,
      currentStoreIdFromLS,
      profileStoreId,
      legacyStoreId,
    });

    return resolvedStoreId;
  }

  // Test method to verify API calls are working
  async testApiConnection(): Promise<boolean> {
    try {
      console.log('Testing API connection...');
      const response = await handleApiCall('GET', '/health', {}, {}, false);
      console.log('API connection test successful:', response);
      return true;
    } catch (error) {
      console.error('API connection test failed:', error);
      return false;
    }
  }
}
